import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, LogOut, Sparkles, Loader2, X, Upload, Mail, Barcode } from "lucide-react";

const empty = { title: "", description: "", price: 0, category: "phones", condition: "new", image_url: "", stock: 1, featured: false, sku: "" };

const formatGBP = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: v >= 100 ? 0 : 2 }).format(Number(v || 0));

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null); // product or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [imgLoading, setImgLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadProducts = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  };
  const loadLeads = async () => {
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadProducts(); loadLeads(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setShowForm(true); };

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), stock: Number(form.stock) };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product added");
      }
      setShowForm(false);
      loadProducts();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); loadProducts(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const generateImage = async () => {
    if (!form.title) { toast.error("Enter a title first"); return; }
    setImgLoading(true);
    try {
      const { data } = await api.post("/admin/generate-image", { title: form.title, description: form.description });
      setForm((f) => ({ ...f, image_url: data.image_url }));
      toast.success("Image generated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImgLoading(false); }
  };

  const onFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_url: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#1a2d5e]">Admin Dashboard</div>
          <h1 className="font-display font-black text-4xl mt-1 text-[#0b1838]">Welcome, {user?.name || "Admin"}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setTab("products"); }} data-testid="admin-tab-products" className={`px-4 py-2 rounded-md text-sm font-semibold ${tab === "products" ? "bg-[#1a2d5e] text-white" : "border border-slate-300"}`}>Products ({products.length})</button>
          <button onClick={() => { setTab("leads"); }} data-testid="admin-tab-leads" className={`px-4 py-2 rounded-md text-sm font-semibold ${tab === "leads" ? "bg-[#1a2d5e] text-white" : "border border-slate-300"}`}>Leads ({leads.length})</button>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold hover:bg-slate-50"><LogOut size={14} /> Logout</button>
        </div>
      </div>

      {tab === "products" && (
        <div className="mt-10">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-display font-bold text-2xl text-[#0b1838]">Products</h2>
            <button onClick={openCreate} data-testid="admin-add-product" className="inline-flex items-center gap-2 px-5 py-3 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838]"><Plus size={16} /> Add product</button>
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white" data-testid="admin-product-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><th className="text-left p-4">Image</th><th className="text-left p-4">Title</th><th className="text-left p-4">Category</th><th className="text-left p-4">Cond.</th><th className="text-left p-4">Price</th><th className="text-left p-4">Stock</th><th className="text-right p-4">Actions</th></tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-row-${p.id}`}>
                    <td className="p-3"><div className="w-14 h-14 bg-slate-100 rounded overflow-hidden">{p.image_url && <img src={p.image_url} className="w-full h-full object-cover" alt="" />}</div></td>
                    <td className="p-4">
                      <div className="font-medium text-[#0b1838]">{p.title}</div>
                      {p.sku ? (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-[#1a2d5e] bg-[#aedfff]/30 border border-[#1a2d5e]/20 px-2 py-0.5 rounded" data-testid={`admin-row-sku-${p.id}`}>
                          <Barcode size={10} /> SKU: {p.sku}
                        </div>
                      ) : (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-400 italic" data-testid={`admin-row-no-sku-${p.id}`}>
                          No POS SKU set
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-slate-500">{p.category}</td>
                    <td className="p-4 text-slate-500">{p.condition}</td>
                    <td className="p-4 font-semibold">{formatGBP(p.price)}</td>
                    <td className="p-4 text-slate-500">{p.stock}</td>
                    <td className="p-4 text-right">
                      <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} className="p-2 hover:bg-slate-100 rounded"><Pencil size={14} /></button>
                      <button onClick={() => remove(p.id)} data-testid={`admin-delete-${p.id}`} className="p-2 hover:bg-red-50 text-red-500 rounded"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "leads" && (
        <div className="mt-10">
          <h2 className="font-display font-bold text-2xl text-[#0b1838] mb-6">Customer leads</h2>
          {leads.length === 0 ? (
            <div className="text-slate-500">No leads yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="admin-leads-list">
              {leads.map((l) => (
                <div key={l.id} className="border border-slate-200 rounded-xl p-5 bg-white" data-testid={`admin-lead-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="overline text-[#1a2d5e]">{l.type}</span>
                    <span className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-2 font-display font-bold text-lg text-[#0b1838]">{l.name}</div>
                  <div className="text-sm text-slate-600">{l.email} {l.phone && `· ${l.phone}`}</div>
                  {l.device && <div className="text-sm text-slate-500 mt-1">Device: {l.device}</div>}
                  {l.extra && <div className="text-xs text-slate-400 mt-1">{JSON.stringify(l.extra)}</div>}
                  {l.message && <div className="text-sm mt-3 text-slate-700">{l.message}</div>}
                  {l.email && <a href={`mailto:${l.email}`} className="mt-3 inline-flex items-center gap-1 text-sm text-[#1a2d5e] font-semibold hover:underline"><Mail size={14} /> Reply</a>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-10 p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-product-form">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display font-bold text-2xl text-[#0b1838]">{editing ? "Edit" : "Add"} product</h3>
              <button onClick={() => setShowForm(false)}><X /></button>
            </div>
            <form onSubmit={submitForm} className="grid gap-4">
              <input required placeholder="Title" data-testid="admin-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />
              <textarea rows={3} placeholder="Description" data-testid="admin-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-lg" />

              <div className="border border-[#1a2d5e]/30 bg-[#aedfff]/15 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Barcode size={18} className="text-[#1a2d5e]" />
                  <label htmlFor="admin-form-sku" className="font-display font-bold text-sm text-[#0b1838]">POS SKU / Stock SKU</label>
                </div>
                <input
                  id="admin-form-sku"
                  placeholder="e.g. IP15 — must match SKU in your POS"
                  data-testid="admin-form-sku"
                  value={form.sku || ""}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg font-mono text-sm bg-white"
                />
                <p className="text-xs text-slate-500 mt-2">Enter the SKU exactly as it appears in your POS. When set, this product page shows live per-branch availability from your POS stock view.</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="overline text-[10px] block mb-1">Price (£ GBP)</label>
                  <input required type="number" step="0.01" placeholder="Price in GBP" data-testid="admin-form-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-3 py-3 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="overline text-[10px] block mb-1">Stock</label>
                  <input required type="number" placeholder="Stock" data-testid="admin-form-stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full px-3 py-3 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="overline text-[10px] block mb-1">Category</label>
                  <select data-testid="admin-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white">
                    <option value="phones">Phones</option>
                    <option value="laptops">Laptops</option>
                    <option value="accessories">Accessories</option>
                  </select>
                </div>
                <div>
                  <label className="overline text-[10px] block mb-1">Condition</label>
                  <select data-testid="admin-form-condition" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white">
                    <option value="new">New</option>
                    <option value="used">Second-hand</option>
                  </select>
                </div>
              </div>

              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="overline">Product image</div>
                  <div className="flex gap-2">
                    <button type="button" onClick={generateImage} disabled={imgLoading} data-testid="admin-form-generate-img" className="inline-flex items-center gap-2 px-3 py-2 bg-[#1a2d5e] text-white text-xs font-semibold rounded hover:bg-[#0b1838] disabled:opacity-60">
                      {imgLoading ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} AI Generate
                    </button>
                    <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 text-xs font-semibold rounded cursor-pointer hover:bg-white" data-testid="admin-form-upload-label">
                      <Upload size={14} /> Upload
                      <input type="file" accept="image/*" className="hidden" onChange={onFileUpload} data-testid="admin-form-upload" />
                    </label>
                  </div>
                </div>
                <input placeholder="…or paste an image URL" data-testid="admin-form-image-url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-xs bg-white" />
                {form.image_url && (
                  <div className="mt-3"><img src={form.image_url} alt="preview" className="max-h-40 rounded border border-slate-200" /></div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="admin-form-featured" />
                Featured on homepage
              </label>

              <button type="submit" disabled={saving} data-testid="admin-form-submit" className="px-6 py-3 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838] disabled:opacity-60">{saving ? "Saving…" : editing ? "Save changes" : "Add product"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
